<div class="w-full max-w-4xl mx-auto flex flex-col gap-4 pb-8">
  <div class="flex items-center justify-between gap-3">
    <div class="flex items-center gap-3">
      <a routerLink="/mit-mandates" class="text-slate-500 hover:text-slate-800" aria-label="Volver">
        <i class="pi pi-arrow-left"></i>
      </a>
      <span class="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-100 text-sky-600">
        <i class="pi pi-credit-card"></i>
      </span>
      <div>
        <h2 class="text-lg font-semibold text-slate-900 m-0">Nuevo mandato</h2>
        <p class="text-sm text-slate-500 m-0">El cliente recibirá por correo una liga para registrar su tarjeta y aceptar los límites.</p>
      </div>
    </div>
  </div>

  @if (configLoaded() && !mitEnabled()) {
    <div class="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex gap-2">
      <i class="pi pi-exclamation-triangle mt-0.5"></i>
      <span>Los cobros bajo demanda no están habilitados para tu compañía. Solicita a Pento que los habilite para poder crear mandatos.</span>
    </div>
  }

  <form [formGroup]="form" (ngSubmit)="save()" class="flex flex-col gap-4">
    <section class="bg-white rounded-xl border border-slate-200 shadow-sm">
      <header class="px-5 py-3 border-b border-slate-200 bg-slate-50/60 rounded-t-xl">
        <h3 class="text-sm font-semibold text-slate-800 m-0">Cliente y propósito</h3>
      </header>
      <div class="p-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div class="flex flex-col gap-1 sm:col-span-2">
          <p-floatlabel class="w-full" variant="on">
            <p-autocomplete
              inputId="mitCustomer"
              formControlName="customer"
              [suggestions]="customerOptions()"
              (completeMethod)="onCustomerSearch($event)"
              (onSelect)="onCustomerSelect($event.value)"
              (onClear)="onCustomerClear()"
              optionLabel="displayLabel"
              [minLength]="3"
              [showClear]="true"
              [invalid]="invalid('customer')"
              fluid
              appendTo="body">
              <ng-template #item let-customer>
                <div class="flex flex-col py-0.5">
                  <span class="text-sm font-medium text-slate-900 leading-snug">{{ customer.comercialName || customer.legalName }}</span>
                  <span class="text-xs text-slate-400 leading-snug">{{ customer.taxId }}</span>
                </div>
              </ng-template>
            </p-autocomplete>
            <label for="mitCustomer">Cliente *</label>
          </p-floatlabel>
          @if (customerLoading()) {
            <small class="text-slate-500">Consultando cliente…</small>
          } @else if (customerWithoutEmail()) {
            <small class="text-amber-700">El cliente no tiene correo electrónico; agrégalo en su ficha para poder enviarle la liga.</small>
          } @else if (selectedCustomer()?.email) {
            <small class="text-slate-500">La liga se enviará a {{ selectedCustomer()?.email }}</small>
          }
        </div>
        <div class="flex flex-col gap-1 sm:col-span-2">
          <p-floatlabel class="w-full" variant="on">
            <input pInputText id="mitPurpose" formControlName="purpose" maxlength="250" class="w-full" [invalid]="invalid('purpose')" />
            <label for="mitPurpose">Concepto que autoriza el cliente *</label>
          </p-floatlabel>
          <small class="text-slate-500">Lo verá el cliente; por ejemplo «Consumo de servicios de mensajería».</small>
        </div>
        <p-floatlabel class="w-full sm:col-span-2" variant="on">
          <input pInputText id="mitReference" formControlName="externalReference" maxlength="100" class="w-full" />
          <label for="mitReference">Referencia externa</label>
        </p-floatlabel>
        <p-floatlabel class="w-full" variant="on">
          <p-datepicker inputId="mitValidFrom" formControlName="validFrom" [showTime]="true" hourFormat="24" dateFormat="dd/mm/yy"
            [minDate]="today" showIcon iconDisplay="input" [showClear]="true" appendTo="body" fluid />
          <label for="mitValidFrom">Vigencia desde</label>
        </p-floatlabel>
        <div class="flex flex-col gap-1">
          <p-floatlabel class="w-full" variant="on">
            <p-datepicker inputId="mitValidUntil" formControlName="validUntil" [showTime]="true" hourFormat="24" dateFormat="dd/mm/yy"
              [minDate]="today" showIcon iconDisplay="input" [showClear]="true" appendTo="body" fluid />
            <label for="mitValidUntil">Vigencia hasta</label>
          </p-floatlabel>
          @if (form.errors?.['validityOrder']) {
            <small class="text-red-600">La vigencia hasta debe ser posterior a la vigencia desde.</small>
          } @else if (form.errors?.['validityRequired']) {
            <small class="text-red-600">Tu compañía limita la vigencia a {{ config()?.maxMandateValidityDays }} días; indica hasta cuándo.</small>
          } @else if (form.errors?.['validityOverCap']) {
            <small class="text-red-600">La vigencia no puede pasar de {{ config()?.maxMandateValidityDays }} días.</small>
          } @else if (config()?.maxMandateValidityDays) {
            <small class="text-slate-500">Vigencia máxima de tu compañía: {{ config()?.maxMandateValidityDays }} días.</small>
          }
        </div>
      </div>
    </section>

    <section class="bg-white rounded-xl border border-slate-200 shadow-sm">
      <header class="px-5 py-3 border-b border-slate-200 bg-slate-50/60 rounded-t-xl">
        <h3 class="text-sm font-semibold text-slate-800 m-0">Cargo al registrar la tarjeta</h3>
        <p class="text-xs text-slate-500 m-0 mt-0.5">
          Cuando el cliente abra la liga y capture su tarjeta, se le hará un cargo para comprobar que la tarjeta funciona.
          Elige si ese cargo es solo de validación o si ya es un primer pago.
        </p>
      </header>
      <div class="p-5 flex flex-col gap-4">
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-3" role="radiogroup" aria-label="Cargo al registrar la tarjeta">
          <button type="button" role="radio" [attr.aria-checked]="!withInitialCharge()" (click)="toggleInitialCharge(false)"
            class="text-left rounded-lg border p-4 transition-colors flex gap-3"
            [ngClass]="!withInitialCharge() ? 'border-sky-500 bg-sky-50' : 'border-slate-200 hover:bg-slate-50'">
            <i class="pi mt-0.5" [ngClass]="!withInitialCharge() ? 'pi-check-circle text-sky-600' : 'pi-circle text-slate-400'"></i>
            <span class="flex flex-col gap-1">
              <span class="text-sm font-semibold text-slate-900">Solo validar la tarjeta</span>
              <span class="text-xs text-slate-600">
                Cargo de {{ (config()?.verificationAmount ?? 1) | currency:'MXN':'symbol-narrow' }} que se cancela de inmediato.
                El cliente no paga nada; no genera venta ni factura. Los cobros llegan después, cuando tú los hagas.
              </span>
            </span>
          </button>
          <button type="button" role="radio" [attr.aria-checked]="withInitialCharge()" (click)="toggleInitialCharge(true)"
            class="text-left rounded-lg border p-4 transition-colors flex gap-3"
            [ngClass]="withInitialCharge() ? 'border-sky-500 bg-sky-50' : 'border-slate-200 hover:bg-slate-50'">
            <i class="pi mt-0.5" [ngClass]="withInitialCharge() ? 'pi-check-circle text-sky-600' : 'pi-circle text-slate-400'"></i>
            <span class="flex flex-col gap-1">
              <span class="text-sm font-semibold text-slate-900">Cobrar un primer pago</span>
              <span class="text-xs text-slate-600">
                El cliente paga los productos que agregues (por ejemplo, inscripción o primer mes) y se registra la venta.
                Si el pago no se aprueba, la tarjeta no queda registrada.
              </span>
            </span>
          </button>
        </div>
        @if (withInitialCharge()) {
          <p-floatlabel class="w-full" variant="on">
            <input pInputText id="mitInitialChargeConcept" formControlName="initialChargeConcept" maxlength="250" class="w-full" />
            <label for="mitInitialChargeConcept">Concepto del primer pago (por defecto, el concepto que autoriza)</label>
          </p-floatlabel>
          <app-mit-charge-lines-fields [lines]="initialChargeLines" idPrefix="mit-initial" />
          @if (form.errors?.['initialOverLimit']) {
            <small class="text-red-600">El primer pago no puede ser mayor al máximo por cobro.</small>
          }
        }
      </div>
    </section>

    <section class="bg-white rounded-xl border border-slate-200 shadow-sm">
      <header class="px-5 py-3 border-b border-slate-200 bg-slate-50/60 rounded-t-xl">
        <h3 class="text-sm font-semibold text-slate-800 m-0">Límites</h3>
        <p class="text-xs text-slate-500 m-0 mt-0.5">Ningún cobro puede rebasarlos. Para subirlos después, el cliente debe aceptarlos.</p>
      </header>
      <div class="p-5">
        <app-mit-limits-fields [form]="limitsForm" idPrefix="mit-create" />
        @if (config()?.maxAmountPerTransactionCap != null || config()?.maxAmountPerMonthPerMandateCap != null) {
          <p class="text-xs text-slate-500 m-0 mt-3">
            Topes de tu compañía:
            @if (config()?.maxAmountPerTransactionCap != null) {
              {{ config()?.maxAmountPerTransactionCap | currency:'MXN':'symbol-narrow' }} por cobro
            }
            @if (config()?.maxAmountPerTransactionCap != null && config()?.maxAmountPerMonthPerMandateCap != null) { y }
            @if (config()?.maxAmountPerMonthPerMandateCap != null) {
              {{ config()?.maxAmountPerMonthPerMandateCap | currency:'MXN':'symbol-narrow' }} por mes por mandato
            }
          </p>
        }
        @if (form.errors?.['perTxOverCap']) {
          <p class="text-sm text-red-600 m-0 mt-2">El máximo por cobro no puede pasar del tope de tu compañía.</p>
        }
        @if (form.errors?.['monthOverCap']) {
          <p class="text-sm text-red-600 m-0 mt-2">El máximo por mes no puede pasar del tope de tu compañía.</p>
        }
      </div>
    </section>

    <section class="bg-white rounded-xl border border-slate-200 shadow-sm">
      <header class="px-5 py-3 border-b border-slate-200 bg-slate-50/60 rounded-t-xl">
        <h3 class="text-sm font-semibold text-slate-800 m-0">Metadata comercial</h3>
        <p class="text-xs text-slate-500 m-0 mt-0.5">
          Opcional. Datos tuyos (por ejemplo matrícula o plantel) que se envían a Paycode con cada cargo del mandato para que
          puedas identificarlos en tu conciliación. El cliente no los ve. No se pueden cambiar después de crear el mandato.
        </p>
      </header>
      <div class="p-5">
        <app-mit-comercial-metadata-fields [rows]="comercialMetadata" idPrefix="mit-create-comercial" />
        @if (comercialMetadata.errors?.['duplicate'] && (comercialMetadata.touched || comercialMetadata.dirty)) {
          <p class="text-sm text-red-600 m-0 mt-2">Cada llave debe ser única.</p>
        }
      </div>
    </section>

    @if (apiError()) {
      <div class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{{ apiError() }}</div>
    }

    <div class="flex justify-end gap-2">
      <a routerLink="/mit-mandates"><p-button label="Cancelar" severity="secondary" [text]="true" type="button" /></a>
      <p-button
        label="Crear mandato"
        icon="pi pi-check"
        type="submit"
        [loading]="saving()"
        [disabled]="saving() || !mitEnabled() || customerWithoutEmail() || customerLoading()" />
    </div>
  </form>
</div>

<p-dialog
  header="Mandato creado"
  [visible]="!!created()"
  [modal]="true"
  [closable]="false"
  [draggable]="false"
  [resizable]="false"
  appendTo="body"
  [style]="{ width: 'min(34rem, calc(100vw - 2rem))' }">
  @if (created(); as mandate) {
    <div class="flex flex-col gap-3 pt-1">
      <p class="text-sm text-slate-600 m-0">
        Enviamos la liga de registro a <span class="font-medium text-slate-900">{{ mandate.customerEmail }}</span>.
        Por seguridad, la liga solo la recibe el cliente; si no le llega, puedes reenviarla desde el detalle del mandato.
      </p>
      @if (mandate.enrollmentExpiresAt) {
        <p class="text-xs text-slate-500 m-0">La liga vence el {{ mandate.enrollmentExpiresAt | date:'dd/MM/yyyy HH:mm' }}.</p>
      }
    </div>
  }
  <ng-template #footer>
    <p-button label="Ver mandato" icon="pi pi-arrow-right" iconPos="right" type="button" (onClick)="goToDetail()" />
  </ng-template>
</p-dialog>
